import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  FiMail,
  FiPhone,
  FiGlobe,
  FiMapPin,
  FiBriefcase,
  FiArrowRight,
} from "react-icons/fi";
import Header from "../Home/Header";
import Footers from "../Home/Footers";
import AxiosInstance from "../Home/AxiosInstance";
import "../Home/publicPages.css";
import "./EmployerDetails.css";

const INITIAL_COLORS = [
  "#5b5fff",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
];

const getInitialColor = (name) => {
  const code = (name || "A").charCodeAt(0) || 65;
  return INITIAL_COLORS[code % INITIAL_COLORS.length];
};

const getLogoUrl = (logo) => {
  if (!logo) return null;
  const path = String(logo).replace(/\\/g, "/");
  if (path.startsWith("http")) return path;
  return `${AxiosInstance.defaults.baseURL}/media/${path}`;
};

const formatWebsite = (url) => {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://${url}`;
};

const formatAddress = (addr) => {
  if (!addr) return "";
  return [
    addr.address_line1,
    addr.address_line2,
    addr.city,
    addr.state?.state_name,
    addr.country?.country_name,
    addr.zip_code,
  ]
    .filter(Boolean)
    .join(", ");
};

export default function EmployerDetails() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [employer, setEmployer] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchEmployer = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await AxiosInstance.get(
          `companies/company/detail/${companyId}/`
        );
        setEmployer(response.data);

        try {
          const addrRes = await AxiosInstance.get(
            `companies/company/addresses/${companyId}/`
          );
          if (Array.isArray(addrRes.data)) setAddresses(addrRes.data);
        } catch {
          setAddresses([]);
        }

        try {
          const internRes = await AxiosInstance.get("companies/internships/");
          const list = Array.isArray(internRes.data) ? internRes.data : [];
          const companyIdNum = Number(companyId);
          setInternships(
            list.filter(
              (item) =>
                item.company?.company_id === companyIdNum ||
                item.company === companyIdNum
            )
          );
        } catch {
          setInternships([]);
        }
      } catch (err) {
        console.error("Failed to load employer:", err);
        setError(
          err.response?.status === 404
            ? "Employer not found."
            : "Failed to load employer details."
        );
      } finally {
        setLoading(false);
      }
    };

    if (companyId) fetchEmployer();
  }, [companyId]);

  if (loading) {
    return (
      <div className="EmployerPage min-vh-100 d-flex flex-column">
        <Header />
        <p className="emp-loading">Loading employer…</p>
        <Footers />
      </div>
    );
  }

  if (error || !employer) {
    return (
      <div className="EmployerPage min-vh-100 d-flex flex-column">
        <Header />
        <div className="emp-loading">
          <p className="text-danger">{error || "Employer not found."}</p>
          <nav className="als-breadcrumb als-breadcrumb-light" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="als-breadcrumb-sep" aria-hidden="true">
              ›
            </span>
            <Link to="/employers">Employers</Link>
          </nav>
        </div>
        <Footers />
      </div>
    );
  }

  const name = employer.company_name || "Employer";
  const logoUrl = getLogoUrl(employer.company_logo);
  const accent = getInitialColor(name);
  const website = formatWebsite(employer.website);
  const businessType =
    employer.business_type?.business_type || "Employer";
  const primaryAddress = addresses[0];

  return (
    <div className="EmployerPage min-vh-100 d-flex flex-column">
      <Header />

      <section className="emp-hero">
        <div className="emp-hero-inner">
          <div className="emp-hero-copy">
            <nav className="als-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span className="als-breadcrumb-sep" aria-hidden="true">
                ›
              </span>
              <Link to="/employers">Employers</Link>
              <span className="als-breadcrumb-sep" aria-hidden="true">
                ›
              </span>
              <span aria-current="page">{name}</span>
            </nav>
            <div className="emp-badges">
              <span className="emp-badge type">Employer</span>
              <span className="emp-badge mode">{businessType}</span>
            </div>
            <h1>{name}</h1>
            {employer.email && (
              <p className="emp-hero-email">{employer.email}</p>
            )}
            <div className="emp-meta">
              {primaryAddress?.city && (
                <div className="emp-meta-item">
                  <FiMapPin /> {primaryAddress.city}
                  {primaryAddress.state?.state_name
                    ? `, ${primaryAddress.state.state_name}`
                    : ""}
                </div>
              )}
              {employer.phone_no_1 && (
                <div className="emp-meta-item">
                  <FiPhone /> {employer.phone_no_1}
                </div>
              )}
              {website && (
                <div className="emp-meta-item">
                  <FiGlobe /> Website available
                </div>
              )}
            </div>
          </div>
          <div className="emp-hero-media">
            <div
              className="emp-hero-logo"
              style={
                !logoUrl
                  ? { background: `${accent}1a`, color: accent }
                  : undefined
              }
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                name.charAt(0).toUpperCase()
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="emp-body flex-grow-1">
        <div className="emp-layout">
          <main className="emp-main">
            <section className="emp-card">
              <h2>About the company</h2>
              <p className="emp-prose">
                {name} partners with Open Learnings to hire
                job-ready talent through internships and industry-aligned
                programs.
              </p>
              <div className="emp-highlights">
                <span>Hiring</span>
                <span>Internships</span>
                <span>{businessType}</span>
              </div>
            </section>

            {addresses.length > 0 && (
              <section className="emp-card">
                <h2>Locations</h2>
                <ul className="emp-list">
                  {addresses.map((addr) => (
                    <li key={addr.address_id}>
                      <FiMapPin className="emp-list-icon" />
                      <div>
                        {addr.address_type?.address_type && (
                          <strong>{addr.address_type.address_type}</strong>
                        )}
                        <p>{formatAddress(addr)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {internships.length > 0 && (
              <section className="emp-card">
                <h2>Open internships</h2>
                <ul className="emp-internship-list">
                  {internships.map((item) => (
                    <li key={item.internship_id}>
                      <div>
                        <strong>{item.role || "Internship"}</strong>
                        <p>
                          {[item.location, item.work_mode, item.duration]
                            .filter(Boolean)
                            .join(" · ") || "Details available on listing"}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="emp-view-role"
                        onClick={() =>
                          navigate(`/internships/${item.internship_id}`)
                        }
                      >
                        View <FiArrowRight />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </main>

          <aside className="emp-sidebar">
            <section className="emp-card emp-contact-card">
              <h2>Contact</h2>
              <ul className="emp-contact-list">
                {employer.email && (
                  <li>
                    <FiMail />
                    <a href={`mailto:${employer.email}`}>{employer.email}</a>
                  </li>
                )}
                {employer.phone_no_1 && (
                  <li>
                    <FiPhone />
                    <a href={`tel:${employer.phone_no_1}`}>
                      {employer.phone_no_1}
                    </a>
                  </li>
                )}
                {employer.phone_no_2 && (
                  <li>
                    <FiPhone />
                    <a href={`tel:${employer.phone_no_2}`}>
                      {employer.phone_no_2}
                    </a>
                  </li>
                )}
                {website && (
                  <li>
                    <FiGlobe />
                    <a href={website} target="_blank" rel="noreferrer">
                      Visit website
                    </a>
                  </li>
                )}
                <li>
                  <FiBriefcase />
                  <span>{businessType}</span>
                </li>
              </ul>
              <button
                type="button"
                className="emp-cta"
                onClick={() => navigate("/internships")}
              >
                Browse internships
              </button>
            </section>
          </aside>
        </div>
      </div>

      <Footers />
    </div>
  );
}
